import { cn } from '@lib/utils';
import { XIcon } from 'lucide-react';
import * as React from 'react';
import { Badge } from './badge';

export type InputChipProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value'> & {
  allowDuplicates?: boolean;
  hasError?: boolean;
  maxValues?: number;
  onEnterKey?: (value: string) => void;
  onValuesChange?: (values: string[]) => void;
  placeholder?: string;
  values?: string[];
};

const InputChip = (props: InputChipProps) => {
  const {
    allowDuplicates = false,
    className,
    hasError = false,
    maxValues,
    onEnterKey,
    onValuesChange,
    placeholder = 'Type and press Enter to add...',
    ref,
    values = [],
    ...otherProps
  } = props;
  const ariaInvalid = hasError || otherProps['aria-invalid'];
  const [inputValue, setInputValue] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const setInputRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const addChip = () => {
    const trimmedValue = inputValue.trim();

    if (trimmedValue.length === 0) return;
    if (maxValues && values.length >= maxValues) return;

    if (!allowDuplicates && values.includes(trimmedValue)) {
      setInputValue('');
      return;
    }

    const newValues = [...values, trimmedValue];
    onValuesChange?.(newValues);
    setInputValue('');
    onEnterKey?.(trimmedValue);
  };

  const removeChip = (index: number) => {
    const newValues = values.filter((_, valueIndex) => valueIndex !== index);
    onValuesChange?.(newValues);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addChip();
    } else if (event.key === 'Backspace' && inputValue === '' && values.length > 0) {
      removeChip(values.length - 1);
    }
  };

  return (
    <div
      aria-invalid={ariaInvalid}
      className={cn(
        'min-h-10 w-full rounded-md border border-gray-200 bg-gray-100 box-border',
        'focus-within:border-gray-500 focus-within:ring-2 focus-within:ring-gray-300',
        'aria-invalid:border-red-500 aria-invalid:focus-within:border-red-500 aria-invalid:focus-within:ring-red-200',
        'disabled:cursor-not-allowed disabled:opacity-50',
        values.length > 0 ? 'p-1' : 'p-0',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-1">
        {values.map((value, index) => (
          <Badge
            key={`${value}-${index}`}
            className="border-transparent bg-gray-200 text-gray-700 hover:bg-gray-200"
            variant="default"
          >
            {value}
            <XIcon
              className="ml-1 size-3 cursor-pointer hover:text-red-500"
              onClick={() => removeChip(index)}
            />
          </Badge>
        ))}
        <input
          aria-invalid={hasError}
          className={cn(
            'flex-1 min-w-0 bg-transparent text-base text-gray-600 md:text-sm placeholder:text-gray-400',
            'border-none outline-none focus:ring-0',
            values.length > 0 ? 'px-1 py-1' : 'px-3 py-2',
          )}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={values.length === 0 ? placeholder : ''}
          ref={setInputRef}
          type="text"
          value={inputValue}
          {...otherProps}
        />
      </div>
    </div>
  );
};

InputChip.displayName = 'InputChip';

export { InputChip };
